import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Cable, Workflow, ScrollText, LogOut, ShieldCheck, Inbox } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/features/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useMobileMenu } from '@/context/MobileMenuContext';

export function InterfacesSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { isSuperAdmin } = usePermissions();
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useMobileMenu();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try { await signOut(); navigate('/'); }
    catch (e) { console.error(e); }
    finally { setIsLoggingOut(false); }
  };

  const navItems = [
    { name: 'חיבור המערכות', path: '/app/interfaces', icon: <Cable className="w-5 h-5" /> },
    { name: 'ממשקים פעילים', path: '/app/interfaces/flows', icon: <Workflow className="w-5 h-5" /> },
    { name: 'הזמנות ממתינות', path: '/app/interfaces/pending', icon: <Inbox className="w-5 h-5" /> },
    { name: 'יומן פעולות', path: '/app/interfaces/log', icon: <ScrollText className="w-5 h-5" /> },
    ...(isSuperAdmin
      ? [{ name: 'סופר אדמין', path: '/app/admin', icon: <ShieldCheck className="w-5 h-5" /> }]
      : []),
  ];

  return (
    <>
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
          fixed md:static inset-y-0 right-0 z-40 md:z-30
          w-64 bg-white border-l md:border-l-0 md:border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-center h-16 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-800">ממשקים</h2>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-all duration-200
                  ${isActive(item.path)
                    ? 'bg-sky-50 text-sky-700 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                `}
              >
                <span className={isActive(item.path) ? 'text-sky-600' : 'text-gray-400'}>
                  {item.icon}
                </span>
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          <div className="p-3 border-t border-gray-200">
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>{isLoggingOut ? 'מתנתק...' : 'התנתק'}</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
